import React, { useContext, useEffect, useState } from "react";
import useSubjectAPI from "./useSubjectAPI";
import RootContext from "@/RootContext";
import SectionDivider from "./SectionDivider";

const NotesSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;
  const purple = "#2E2868";
  
  const rootContext = useContext(RootContext);
  const auth = rootContext.get("auth");

 
  const currentUserName = (auth?.given_name || auth?.family_name) 
    ? `${auth.given_name || ""} ${auth.family_name || ""}`.trim() 
    : (auth?.name || "System User");


  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [open, setOpen] = useState(true);
  const [notes, setNotes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState({ note: "" });
  const [isSaving, setIsSaving] = useState(false);

  const sortNotes = (list) => {
    return [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      if (!res?.subject_notes) return;
      setNotes(sortNotes(res.subject_notes));
    });
  }, [isCreate, subjectId]);

  useEffect(() => {
    if (!isCreate || !form?.setValue) return;
    form.setValue({
      key: "subject_notes",
      value: notes.map((n) => ({
        note: n.note,
        created_by: n.created_by,
        created_at: n.created_at,
      })),
    });
  }, [notes]);

  const saveNote = async () => {
    if (!newNote.note.trim()) return;

    setIsSaving(true);
    
    const timestamp = new Date().toISOString();
    const authorName = currentUserName; 

    // Optimistic Update
    const tempNote = {
        id: Date.now(), 
        note: newNote.note,
        created_by: authorName, 
        created_at: timestamp,
        subject_id: subjectId
    };

    setNotes((prev) => [tempNote, ...prev]);
    setNewNote({ note: "" });
    setShowAddForm(false);

    if (isCreate) {
      setIsSaving(false);
      return;
    }

    // Prepare Payload
    const payload = {
      subjects: { id: subjectId }, 
      subject_notes: [
        { 
          subject_id: subjectId, 
          note: newNote.note, 
          created_by: authorName 
        }
      ],
    };

    try {
      const res = await saveSubjectDirect(payload);
      
      const updatedSubject = res?.subjects?.[0];

      if (updatedSubject && updatedSubject.subject_notes) {
        setNotes(sortNotes(updatedSubject.subject_notes));
      } else {
        
      }
    } catch (e) {
      console.error("❌ Exception during save:", e);
    
    }
    
    setIsSaving(false);
  };
  
  const inputStyle = {
    backgroundColor: "#fff",
    border: `2px solid ${purple}`,
    boxShadow: "0 0 5px rgba(46, 40, 104, 0.2)",
    padding: "0.5rem 1rem",
  };

  /* RENDER */
  return (
    <div className="my-3" style={{ maxWidth: 490 }}>
      <div className="p-3">
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => setOpen((o) => !o)}
        >
          <div className="d-flex align-items-center">
            <span style={{ fontSize: 30, color: "#b2a9ff", marginRight: 6 }}>⋮⋮</span>
            <h5 className="fw-bold m-0" style={{ color: purple }}>Notes</h5>
          </div>
          <div
            style={{
              width: 28, height: 28, borderRadius: 8,
              backgroundColor: purple, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: 14 }} />
          </div>
        </div>

        {/* Content */}
        {open && (
          <>
            <div className="text-end mb-2">
              <span
                onClick={() => setShowAddForm(true)}
                style={{ fontSize: 13, color: purple, cursor: "pointer", fontWeight: 600 }}
              >
                + Add
              </span>
            </div>

            {/* ADD FORM */}
            {showAddForm && (
              <div className="p-3 mb-3 position-relative" style={{ background: "#f2f2f7", borderRadius: 12 }}>
                <span
                  onClick={() => !isSaving && setShowAddForm(false)}
                  style={{
                    position: "absolute", right: "12px", top: "10px",
                    cursor: isSaving ? "default" : "pointer",
                    color: purple, fontWeight: "bold", fontSize: "23px", opacity: isSaving ? 0.5 : 1
                  }}
                >
                  ×
                </span>

                <div className="mb-2">
                  <label className="mb-1" style={{fontSize: 12, fontWeight: 'bold'}}>Added by</label>
                  <input
                    disabled
                    value={currentUserName}
                    className="form-control rounded-pill border-0"
                    style={{ backgroundColor: "#e9ecef", fontSize: 13 }}
                  />
                </div>

                <div className="mb-2">
                  <label className="mb-1" style={{fontSize: 12, fontWeight: 'bold'}}>Date / Time</label>
                  <input
                    disabled
                    value={new Date().toLocaleString()}
                    className="form-control rounded-pill border-0"
                    style={{ backgroundColor: "#e9ecef", fontSize: 13 }}
                  />
                </div>

                <div className="mb-3">
                  <label className="mb-1" style={{fontSize: 12, fontWeight: 'bold'}}>Note</label>
                  <textarea
                    rows={4}
                    disabled={isSaving}
                    value={newNote.note}
                    onChange={(e) => setNewNote({ note: e.target.value })}
                    className="form-control rounded-4"
                    style={inputStyle}
                    placeholder="Write your note here..."
                  />
                </div>

                <div className="d-flex justify-content-center gap-3">
                  <button
                    className="btn btn-sm"
                    onClick={saveNote}
                    disabled={isSaving}
                    style={{
                      background: purple, color: "#fff", borderRadius: 20,
                      padding: "4px 24px", opacity: isSaving ? 0.7 : 1, cursor: isSaving ? "wait" : "pointer"
                    }}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            )}

            {/* LIST */}
            {notes.length === 0 && !showAddForm && (
              <div style={{ fontSize: 13, opacity: 0.6, padding: "10px 0" }}>No notes added yet</div>
            )}

            {notes.map((note, idx) => (
              <div key={note.id || idx}>
                <div className="mb-2">
                  <div
                    className="d-flex justify-content-between"
                    style={{ fontSize: 13, fontWeight: 600 }}
                  >
                    {/* Display Author */}
                    <span>Note by {note.created_by || "System User"}</span>
                    <span style={{ opacity: 0.7 }}>
                      {new Date(note.created_at).toLocaleString()}
                    </span>
                  </div>

                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {note.note}
                  </div>
                </div>

                {idx !== notes.length - 1 && (
                  <SectionDivider width="380px" />
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default NotesSection;